import Hooks from '@player/src/hooks/index.js';
import {getErrorDetails} from "@player/src/errors/errorDetails.js";
import {getLangDisplayList,getQualityDisplayList} from '@player/src/displayMixins.js';

const fetch_shaka=()=>import(/* webpackChunkName: "shaka" */ /* webpackMode: "lazy" */ "shaka-player");

/**
 * @class @function DashPlayer
 * * 
 * @property  {string} playerType: <required> @identifier
 * @property {Instance of Hooks} hub:<required>
 * @property {HTMLVideoElement} _videoTag
 * @property {Object} _settings
 * @property {Object} _content:<assigned on load>
 * 
 * ------------------------------------------
 * @lifecycle @methods :
 * 
 * @method instantiate: 
 * - imports and creates shaka.Player instance. 
 * - @returns Promise on completion.
 *
 * @method load: @param {Object} content: content to be loaded.
 * - requests drmToken for the content. 
 * - @returns a Promise resolved after content is loaded.
 * 
 * @method unload: 
 * - unloads the player from the current content. 
 * - @returns a Promise resolved on completion.
 * 
 * @method destroy:
 * - destroys the player and removes the instance of player.
 * - @returns a Promise resolved on completion.
 * 
 * ---------------------------------------------
 * @internal @methods :
 * 
 * @method _loadPlayer: @param {Object} isDrmContent
 * - internal method called inside @method load .
 * - configures the player.
 * - loads the content to be played. 
 *   @returns a Promise. 
 * 
 * -----------------------------------------------------------------------------------------------
 * @dependency_injection @methods :
 * 
 * @method _filterRequests : @type {optional} 
 * - called on each request made by drm.
 * - recieves @argument type,@argument request  
 */

class DashPlayer{

  static ignoreErrors = [ 1002, 7000 ];
  
  constructor({videoTag,settings}){
    this.playerType='dashPlayer';
    this.hub=new Hooks();
    if(!videoTag || !settings ){
      this.hub.dispatch('error',getErrorDetails(0));
      throw { errorDispatched: true }
    }
    this._videoTag=videoTag;
    this._settings=settings;
    this._content=null;
  
  }
  instantiate(){
    return fetch_shaka().then(
         shaka => {
             //console.log("SHAKA SUPPORT",shaka.Player.isBrowserSupported())
             this._shaka=shaka;
             this._shakaInstance=new this._shaka.Player(this._videoTag);
             if(this._filterRequests){
               this._shakaInstance.getNetworkingEngine().registerRequestFilter((type,request)=>{
                 this._filterRequests(type,request);
               });
             }
             this._attachShakaListeners()
         });
  }
  _attachShakaListeners(){
    this._shakaInstance.addEventListener("streaming", () => {
       
        //this.hub.dispatch('subtitleList', getLangDisplayList(this._shakaInstance.getTextLanguages()) );
        this.hub.dispatch('qualityList', 
                          getQualityDisplayList(this._shakaInstance.getVariantTracks(), this._content?.contentQuality) );
        this.hub.dispatch('audioList', getLangDisplayList(this._shakaInstance.getAudioLanguages()) );  

        // console.log("subtitleList", this._shakaInstance.getTextLanguages());
        // console.log("qualityList", this._shakaInstance.getVariantTracks());
        // console.log("audioList", this._shakaInstance.getAudioLanguages());
    });
    this._shakaInstance.addEventListener('error',(error)=>{
      console.log(error);
      if( DashPlayer.ignoreErrors.includes( error?.detail?.code ) )return ; 
      this.hub.dispatch('error',getErrorDetails(6));
    })
  }

  selectAudio(val){
    if (!val) {
      this._shakaInstance.selectAudioLanguage("en");
    } 
    else {
      this._shakaInstance.selectAudioLanguage(val);
    }
  }
  selectSubtitle(val){
    if (!val) {
      this._shakaInstance.setTextTrackVisibility(false);
    } 
    else {
      this._shakaInstance.selectTextLanguage(val);
      this._shakaInstance.setTextTrackVisibility(true);
    }
  }
  selectQuality(selectedTrack){
    const playerConfiguration = this._shakaInstance.getConfiguration();
    
    playerConfiguration.abr.enabled = !selectedTrack;

    this._shakaInstance.configure(playerConfiguration);
    if(selectedTrack)this._shakaInstance.selectVariantTrack(selectedTrack);

  }

  load(content){
    this._content=content;
    
    return  this._loadPlayer(content).catch((err)=>{
                let errorcode;
                switch(err){
                  case 401:errorcode=2;break;
                  case 402:errorcode=3;break;
                  default: errorcode=4;  
                }  
                this.hub.dispatch('error',getErrorDetails(errorcode));
                console.log('error',err);
                return Promise.reject({errorDispatched:true}); //pass error forwards.
            })
  }

  _loadPlayer(content){
    this._shakaInstance.configure({
        abr: {
          enabled: true
        },
        drm: {
          servers: {
            "com.widevine.alpha": this._settings.widevineLicenseURL
          }
        }
      });
      
      return this._shakaInstance.load(content.getSource())
  }

  unload(){
    return this._shakaInstance?.unload().then(()=>{
      this._content=null;
    });
  }
  destroy(){
    if(!this.playerType)return Promise.resolve();//avoid recurring destroy calls.
    
    this.hub.destroy();
    this.playerType=null;//remove identification.

    return this._shakaInstance?.destroy().then(() => {
        this._shakaInstance = null;
        this._content=null;
        this._videoTag=null;
      });
  }

 /** Methods to be Overridden: */ 
  // _filterRequests(){
  //     throw new Error('No _filterRequests() method available in class DashPlayer');
  // }

}



export  default DashPlayer;